import {BankCodeType, FinancialInvestmentCompanyCodeType, PendingPaymentStatus} from '@web/core/constants'
import {AccountState, pendingPaymentAction} from '@web/slices/account/slice'
import makeSlice from '@web/slices/account/sliceMaker'

import {initOrderSheetByAPI} from '../../actions'
import {PayMethod} from '../../apis/response/interface'
import {BankEasyPayView} from '../../apis/response/interface/payMethod/bankEasyPay'

/**
 * 기본결제수단의 충전결제 계좌를 선택합니다.
 * - 디폴트 계좌가 점검중이라면, 점검중 아닌 계좌로 노출
 * https://oss.fin.navercorp.com/payOrder/order-qa/issues/2377#issuecomment-365589
 */
function getSelectedRechargeAccount(accounts: BankEasyPayView[]): BankEasyPayView | undefined {
    const result = accounts[0]
    if (result && !result.payable) {
        const account = accounts.find(({payable}) => payable)
        if (account) {
            return account
        }
    }
    return result
}

const convertEasyAccount = (bankEasyPays: BankEasyPayView[]): AccountState['bankEasyPay']['accounts'] => {
    return bankEasyPays.map((bankEasyPay) => {
        return {
            accountKey: bankEasyPay.accountKey,
            associatedCheckCard: bankEasyPay.associatedCheckCard,
            bankCode: bankEasyPay.bankCode as BankCodeType | FinancialInvestmentCompanyCodeType,
            bankName: bankEasyPay.bankName,
            maskedAccountNumber: bankEasyPay.maskedAccountNumber,
            nickName: bankEasyPay.nickName,
            payPartnershipItemCode: bankEasyPay.payPartnershipItemCode,
            payPartnershipItemName: bankEasyPay.payPartnershipItemName,
            payServiceMaintenanceSchedule: bankEasyPay.payServiceMaintenanceSchedule,
            payable: bankEasyPay.payable,
            pendingPayable: bankEasyPay.pendingPayable,
            registrationCompletedDateTime: bankEasyPay.registrationCompletedDateTime,
            openBankingAgreed: true,
        }
    })
}

const getPendingPaymentInfo = (
    pendingPaymentInfo: PayMethod['pendingPaymentInfo'],
): AccountState['pendingPaymentInfo'] => {
    if (pendingPaymentInfo) {
        const bankEasyPayPendingPayable = pendingPaymentInfo?.bankEasyPayPendingPayable ?? false
        const payMoneyPendingPayable = pendingPaymentInfo?.payMoneyPendingPayable ?? false
        const hasWaitingPaymentOrder = !bankEasyPayPendingPayable || !payMoneyPendingPayable

        return {
            hasWaitingPaymentOrder,
            status: hasWaitingPaymentOrder ? PendingPaymentStatus.UNABLE : PendingPaymentStatus.ENABLE,
        }
    }
    return {
        hasWaitingPaymentOrder: null,
        status: PendingPaymentStatus.DISABLE,
    }
}

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(initOrderSheetByAPI.fulfilled, (state, {payload}) => {
                const {payMethod, naverpayPoint} = payload.data
                state.maintenanceInfo = payMethod.bankEasyPayServiceMaintenanceSchedule
                state.naverBankbookBenefitOnRecentPayment = naverpayPoint?.naverBankbookBenefitOnRecentPayment ?? false

                state.bankEasyPay.accounts = convertEasyAccount(payMethod.recentUsedFirstBankEasyPays)
                state.bankEasyPay.selectedAccountKey = payMethod.recentUsedFirstBankEasyPays[0]?.accountKey ?? ''
                state.recharge.accounts = convertEasyAccount(payMethod.recentChargedAccountFirstBankEasyPays)

                state.pendingPaymentInfo = getPendingPaymentInfo(payMethod?.pendingPaymentInfo)

                /** 충전계좌 목록의 첫번쩨 계좌를 선택된 상태로 초기화 */
                const selectedRechargeAccount = getSelectedRechargeAccount(
                    payMethod.recentChargedAccountFirstBankEasyPays,
                )
                state.recharge.selectedAccountKey = selectedRechargeAccount?.accountKey ?? ''
                if (payMethod.billingPayMethods.bankEasyPay) {
                    state.bankEasyPay.billing = {
                        pgCp: payMethod.billingPayMethods.bankEasyPay.pgCp,
                        pgId: payMethod.billingPayMethods.bankEasyPay.pgId,
                        minimumAmount: payMethod.billingPayMethods.bankEasyPay.minimumAmount,
                        maximumAmount: payMethod.billingPayMethods.bankEasyPay.maximumAmount,
                    }
                }
            })
            .addCase(pendingPaymentAction, (state, {payload}) => {
                state.pendingPaymentInfo.status = payload.pendingPaymentStatus
            })
    },
})

export default slice.reducer
